import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShareAlt, faCopy, faEnvelope, faHeart } from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import useTokenStore from '../../store/store';
import { toggleFavoriteProperty } from '../../utils/favourites';
import { useFavoritesStore } from '../../store/favoritesStore';
import { useNavigate } from 'react-router-dom';
import useThemeStore from '../../store/themeStore';
import { ThemeProps } from '../../utils/theme';
import { doesThemeSkipLogin } from '../../store/themesWithoutLogin';

interface PropertyShareIconsProps {
  propertyId: string;
}

const PropertyShareIcons: React.FC<PropertyShareIconsProps> = ({ propertyId }) => {
  const [shareExpanded, setShareExpanded] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [heartPulse, setHeartPulse] = useState(false);
  const getToken = useTokenStore((state) => state.token);
  const navigate = useNavigate();
  const { favoriteIds, fetchFavorites } = useFavoritesStore();
  const getThemes = useThemeStore((state) => state.theme as unknown as ThemeProps);
  const isFavorite = favoriteIds.includes(propertyId);

  useEffect(() => {
    if (shareExpanded) {
      const timer = setTimeout(() => {
        setShareExpanded(false);
      }, 20000); // 20 seconds
      return () => clearTimeout(timer);
    }
  }, [shareExpanded]);

  useEffect(() => {
    if (modalVisible) {
      const timer = setTimeout(() => {
        setModalVisible(false);
      }, 5000); // 5 seconds
      return () => clearTimeout(timer);
    }
  }, [modalVisible]);

  const toggleShareOptions = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShareExpanded(!shareExpanded);
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    const link = `${window.location.origin}/listing/${propertyId}`;
    navigator.clipboard.writeText(link);
    setModalVisible(true);
  };

  const handleWhatsAppShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const link = `${window.location.origin}/listing/${propertyId}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(link)}`, '_blank');
  };

  const handleEmailShare = (e: React.MouseEvent) => {
    e.stopPropagation();
    const link = `${window.location.origin}/listing/${propertyId}`;
    window.location.href = `mailto:?subject=Check out this property&body=${encodeURIComponent(link)}`;
  };

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const themeSkipsLogin = doesThemeSkipLogin(getThemes);
    if (themeSkipsLogin) return;

    if (!getToken) {
      sessionStorage.setItem('intendedPropertyId', propertyId);
      navigate("/login");
      return;
    }
    
    try {
      // Visual feedback before API response
      setHeartPulse(true);
      setTimeout(() => setHeartPulse(false), 800);
      
      const response = await toggleFavoriteProperty(propertyId, getToken);
      if (response.status === 200) {
        fetchFavorites(); // Refresh favorites list
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  const isWhiteOrShade = (color: string) => {
    if (color.toLowerCase() === "white" || color === "rgba(255,255,255,1)") return true;
    if (/^#fff[a-f0-9]{0,3}$/i.test(color)) return true;
    const rgba = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+),?\s*([\d.]+)?\)/);
    if (rgba) {
      const [, r, g, b] = rgba.map(Number);
      return r >= 240 && g >= 240 && b >= 240;
    }
    return false;
  };

  const iconStyle = {
    transition: 'transform 0.2s',
    fontSize: '1.2rem',
    marginLeft: '8px',
  };

  return (
    <div className="flex items-center relative" onClick={(e) => e.stopPropagation()}>
      {/* Heart Icon */}
      <button
        className={`focus:outline-none transition-all duration-300 ${
          heartPulse ? 'animate-pulse scale-110' : ''
        } ${isFavorite ? 'cursor-wait' : 'hover:scale-125 hover:shadow-xl active:scale-90'}`}
        onClick={handleToggleFavorite}
        style={{
          background: isFavorite 
            ? `${isWhiteOrShade(getThemes?.color1) ? '#000000' : getThemes?.color1 || '#ffffff'}`
            : '#ffffff',
          padding: '8px',
          borderRadius: '50%',
          width: '36px',
          height: '36px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: isFavorite 
            ? '0 4px 10px rgba(0, 0, 0, 0.25), 0 0 15px rgba(0, 0, 0, 0.2)' 
            : '0 4px 8px rgba(0, 0, 0, 0.15)',
          cursor: 'pointer',
          marginRight: '12px',
          border: '1px solid rgba(0, 0, 0, 0.1)',
        }}
      >
        <FontAwesomeIcon 
          icon={faHeart} 
          className="text-lg transition-all duration-300"
          style={{
            color: isFavorite 
              ? '#ffffff' // White heart on theme color background
              : '#000000', // Black when not favorited
            filter: isFavorite ? 'drop-shadow(0 0 2px rgba(255, 255, 255, 0.5))' : 'none',
          }}
        />
      </button>

      {/* Share Icon */}
      <button
        className="text-blue-500 hover:text-blue-700 focus:outline-none"
        style={{
          ...iconStyle,
          marginLeft: '0',
        }}
        onClick={toggleShareOptions}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <FontAwesomeIcon icon={faShareAlt} />
      </button>

      {shareExpanded && (
        <div className="flex items-center ml-4">
          <div className="mx-4 h-6 border-l border-gray-300"></div>
          <button
            className="text-blue-500 hover:text-blue-700 focus:outline-none"
            style={iconStyle}
            onClick={handleCopyLink}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
          <div className="mx-4 h-6 border-l border-gray-300"></div>
          <button
            className="text-blue-500 hover:text-blue-700 focus:outline-none"
            style={iconStyle}
            onClick={handleWhatsAppShare}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <FontAwesomeIcon icon={faWhatsapp} />
          </button>
          <div className="mx-4 h-6 border-l border-gray-300"></div>
          <button
            className="text-blue-500 hover:text-blue-700 focus:outline-none"
            style={iconStyle}
            onClick={handleEmailShare}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.3)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            <FontAwesomeIcon icon={faEnvelope} />
          </button>
          <div className="mx-4 h-6 border-l border-gray-300"></div>
        </div>
      )}

      {modalVisible && (
        <div
          className="fixed inset-0 flex items-center justify-center z-50"
          onClick={(e) => {
            e.stopPropagation();
            setModalVisible(false);
          }}
        >
          <div className="absolute inset-0 bg-black opacity-50"></div>
          <div
            className="bg-white p-4 rounded shadow-lg relative z-60" style={{ backgroundColor: "white" }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold mb-4">Link Copied</h2>
            <p>This property's link has been copied to your clipboard</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyShareIcons;
